import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LineChart, TrendingUp, TrendingDown } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function SpendTrends({ refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/snapbudget/trends?months=6").then((r) => setData(r.data.months)).catch(() => {});
  }, [refreshKey]);

  if (!data) return null;
  const nets = data.map((d) => d.net);
  const min = Math.min(...nets, 0), max = Math.max(...nets, 0);
  const range = (max - min) || 1;
  const W = 600, H = 130, pad = 16;
  const pts = data.map((d, i) => {
    const x = pad + i * ((W - 2 * pad) / Math.max(1, data.length - 1));
    const y = H - pad - ((d.net - min) / range) * (H - 2 * pad);
    return [x, y];
  });
  const path = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${path} L${pts[pts.length - 1][0].toFixed(1)},${H - pad} L${pts[0][0].toFixed(1)},${H - pad} Z`;
  const last = nets[nets.length - 1] ?? 0, prev = nets[nets.length - 2] ?? 0;
  const up = last >= prev;
  const zeroY = H - pad - ((0 - min) / range) * (H - 2 * pad);

  return (
    <section data-testid="snap-trends" className="bg-[#0d1730] border border-emerald-500/20 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-emerald-950/40">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold tracking-wide text-emerald-300 flex items-center gap-2">
          <LineChart className="w-5 h-5" /> Fluid Balance Trend
        </h2>
        <span className={`text-xs flex items-center gap-1 ${up ? "text-emerald-400" : "text-rose-400"}`}>
          {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {up ? "Climbing" : "Slipping"}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" preserveAspectRatio="none">
        <line x1={pad} y1={zeroY} x2={W - pad} y2={zeroY} stroke="rgba(255,255,255,0.12)" strokeDasharray="4 4" />
        <path d={area} fill="rgba(16,185,129,0.12)" />
        <path d={path} fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill={nets[i] >= 0 ? "#34d399" : "#fb7185"} />
        ))}
      </svg>
      <div className="grid grid-cols-6 gap-1 mt-2 text-center">
        {data.map((d, i) => (
          <div key={i} className="text-[10px]">
            <div className="text-slate-500">{d.label}</div>
            <div className={`font-mono ${d.net > 0 ? "text-emerald-400 cc-gold-positive" : d.net === 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(d.net)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
